'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { FlaskConical, ArrowLeft } from 'lucide-react';
import { AbSplitView } from '@/components/funnels/AbSplitView';

interface FunnelMeta { id: string; name: string; baseUrl: string; keyConfigured: boolean; }

/** A/B Tests — drill-through page. Global: every funnel. Inside a project
 *  tab, `onlyFunnelId` scopes to that project's funnel (no cross-bleed). */
export default function AbTestsPage({ onlyFunnelId }: { onlyFunnelId?: string } = {}) {
  const [funnels, setFunnels] = useState<FunnelMeta[]>([]);
  const [stats, setStats] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const d = await fetch('/api/funnels').then(r => r.json()).catch(() => ({ funnels: [] }));
      let fs: FunnelMeta[] = d.funnels || [];
      if (onlyFunnelId) fs = fs.filter(f => f.id === onlyFunnelId);
      setFunnels(fs);
      const all: Record<string, any> = {};
      await Promise.all(fs.map(async f => {
        const s = await fetch(`/api/funnels/${f.id}/stats`).then(r => r.json()).catch(() => null);
        if (s?.stats) all[f.id] = s.stats;
      }));
      setStats(all);
      setLoading(false);
    })();
  }, []);

  return (
    <div className="space-y-6 max-w-6xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-white flex items-center gap-2"><FlaskConical className="w-5 h-5 text-cyan-400" /> A/B Tests</h1>
          <p className="text-sm text-gray-500 mt-1">{onlyFunnelId ? 'Live splits for this funnel. One change per test → winner → next drop-off.' : 'Every live split across all funnels. One change per test → winner → next drop-off.'}</p>
        </div>
        <Link href="/funnels" className="text-xs text-gray-400 hover:text-white flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Funnels</Link>
      </div>

      {loading && <p className="text-sm text-gray-500">Loading tests…</p>}

      {!loading && funnels.map(f => {
        const tests = stats[f.id]?.['ab-tests']?.tests || [];
        return (
          <div key={f.id} className="glass-card rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-medium text-white">{f.name}</h2>
              {!f.keyConfigured && <span className="text-[10px] text-yellow-300/80">no API key — stats unavailable</span>}
            </div>
            {tests.length ? (
              <AbSplitView funnelId={f.id} origin={new URL(f.baseUrl).origin} tests={tests} />
            ) : (
              <p className="text-xs text-gray-500">No A/B tests running.</p>
            )}
          </div>
        );
      })}
    </div>
  );
}
